// Report Requests — TanStack Query hooks over reportRequestsApi.
//
// Every mutation invalidates the loan's overview, because the tab reads the
// whole payload (watch state + tax date + every request with its items) in one
// call and any write can move more than one part of it: approving an item
// rolls its request's status forward server-side, so a hook that only patched
// the item back would leave the row's status and its approved/outstanding
// counts stale.
//
// Writes that change a discipline's facts additionally refresh the single
// loan-health severity source, per the stale-after-save convention. Approving
// an item files a Document into a DMS folder, so it also refreshes the DMS
// document scope.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  reportRequestsApi,
  type ApproveReportRequestItemRequest,
  type IssueReportRequestRequest,
  type ReportRequestType,
  type SetDscrWatchRequest,
} from "@/api/portfolioAdmin/reportRequestsApi";
import { reportRequestKeys } from "./reportRequestKeys";
import { invalidateDmsDocumentScope, invalidateLoanHealth } from "./invalidation";

// --- Reads -------------------------------------------------------------------

export function useLoanReportRequests(loanId: string | undefined) {
  return useQuery({
    queryKey: reportRequestKeys.overview(loanId ?? ""),
    queryFn: () => reportRequestsApi.getOverview(loanId!),
    enabled: !!loanId,
  });
}

/**
 * Issue-dialog options for one type. `enabled` is the dialog's open state —
 * these are only fetched while B3 is actually choosing, and re-fetched when
 * they switch type.
 */
export function useReportRequestIssueOptions(
  loanId: string | undefined,
  type: ReportRequestType | null,
  enabled = true,
) {
  return useQuery({
    queryKey: reportRequestKeys.issueOptions(loanId ?? "", type ?? ""),
    queryFn: () => reportRequestsApi.getIssueOptions(loanId!, type!),
    enabled: !!loanId && !!type && enabled,
  });
}

// --- Writes ------------------------------------------------------------------

export function useIssueReportRequest(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueReportRequestRequest) =>
      reportRequestsApi.issue(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useCompleteRequestWithExceptions(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note: string }) =>
      reportRequestsApi.completeWithExceptions(requestId, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useCancelReportRequest(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note: string }) =>
      reportRequestsApi.cancel(requestId, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}

/** Approve-and-file one item. Files a Document, so the DMS scope refreshes
 *  alongside the overview and loan health. */
export function useApproveReportRequestItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body: ApproveReportRequestItemRequest;
    }) => reportRequestsApi.approveItem(itemId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
      void invalidateDmsDocumentScope(qc);
    },
  });
}

/** Return ONE item to the borrower. Its already-approved siblings are
 *  untouched — only this row reopens in their checklist. */
export function useReturnReportRequestItem(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, note }: { itemId: string; note: string }) =>
      reportRequestsApi.returnItem(itemId, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}

/**
 * Write the AI summary report. Only the overview is invalidated: generation
 * moves no loan-health input — it neither files a document nor changes an
 * item's state — so refreshing health here would be a request that can only
 * ever return the same answer.
 */
export function useGenerateReportRequestReport(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => reportRequestsApi.generateReport(requestId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
    },
  });
}

/**
 * Finalise and file. This one DOES file a Document into a DMS folder and moves
 * the request to its terminal state, so the DMS scope and loan health both
 * refresh alongside the overview.
 */
export function useFinaliseReportRequestReport(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, folderId }: { requestId: string; folderId: string }) =>
      reportRequestsApi.finaliseReport(requestId, folderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
      void invalidateDmsDocumentScope(qc);
    },
  });
}

export function useSetDscrWatch(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetDscrWatchRequest) =>
      reportRequestsApi.setDscrWatch(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}

/**
 * Record (or clear) the loan's tax-completion date. This gates the Annual
 * request — its due date is that date + 30 days — so the issue dialog's
 * options must be re-fetched too, not just the overview.
 */
export function useRecordTaxesCompleted(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taxesCompletedAt: string | null) =>
      reportRequestsApi.recordTaxesCompleted(loanId, taxesCompletedAt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportRequestKeys.overview(loanId) });
      void qc.invalidateQueries({ queryKey: reportRequestKeys.all() });
      void invalidateLoanHealth(qc, loanId);
    },
  });
}
